import type { Node, NodeId } from "~/models/dto";
import { ImmutableMap } from "~/tools/immutable-map";

import type { TypedAction } from "../../../generic/redux/TypedRedux";

import type { GlobalState, NodeMap } from "./GlobalState";

export interface GlobalChangedData<T extends Node = any> {
	addedOrUpdated?: Array<T>;
	deleted?: Array<NodeId>;
}

export type GlobalChangedAction<
	ActionName extends string = any,
	NodeType extends Node = any,
> = GlobalChangedData<NodeType> & TypedAction<ActionName>;

export function createChangedReducer<A extends GlobalChangedAction<any, N>, N extends Node>(
	getFn: (state: GlobalState) => NodeMap<N>,
	setFn: (state: GlobalState, newMap: NodeMap<N>) => GlobalState,
) {
	return (state: GlobalState, { addedOrUpdated, deleted }: A): GlobalState => {
		const currentMap = getFn(state);
		const mergedMap = addedOrUpdated
			? currentMap.union(ImmutableMap.fromList(({ id }) => id!, addedOrUpdated))
			: currentMap;
		return setFn({ ...state }, deleted ? mergedMap.delete(...deleted) : mergedMap);
	};
}

export type GlobalChangedActionFactory<
	Action extends GlobalChangedAction<any, NodeType> = any,
	NodeType extends Node = any,
> = (data: GlobalChangedData<NodeType>) => Action;

export interface ChangeActionSpec<
	Action extends GlobalChangedAction<ActionName, NodeType> = any,
	ActionName extends string = any,
	NodeType extends Node = any,
> {
	actionName: ActionName;
	nodeType: string;
	factory: GlobalChangedActionFactory<Action, NodeType>;
	reducer: (state: GlobalState, action: Action) => GlobalState;
}

export function createActionSpec<
	Action extends GlobalChangedAction<ActionName, NodeType>,
	ActionName extends string,
	NodeType extends Node,
>(
	actionName: ActionName,
	nodeType: string,
	getFn: (state: GlobalState) => NodeMap<NodeType>,
	setFn: (state: GlobalState, newMap: NodeMap<NodeType>) => GlobalState,
): ChangeActionSpec<Action, ActionName, NodeType> {
	return {
		actionName,
		nodeType,
		factory: (data) => ({ ...data, type: actionName }) as Action,
		reducer: createChangedReducer<Action, NodeType>(getFn, setFn),
	};
}
